import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import './Setup.css';

function Setup() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [dealershipName, setDealershipName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      setValidating(true);
      const response = await api.get(`/onboarding/setup/${token}`);

      if (response.data.valid) {
        setTokenValid(true);
        setDealershipName(response.data.dealership_name || '');
        setEmail(response.data.email || '');
      } else {
        setError(response.data.message || 'Invalid setup link');
      }
    } catch (err) {
      setError('Unable to validate setup link. Please contact support.');
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!password) {
      setError('Please create a password');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);

      const response = await api.post('/onboarding/activate', {
        token,
        email,
        password
      });

      if (response.data.success) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.message || 'Activation failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Activation failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || validating) {
    return (
      <div className="setup-container">
        <div className="setup-card">
          <div className="setup-loading">
            <div className="spinner"></div>
            <p>Validating your setup link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="setup-container">
        <div className="setup-card">
          <div className="setup-error-state">
            <div className="error-icon">!</div>
            <h2>Setup Link Invalid</h2>
            <p>{error}</p>
            <p className="error-help">
              If you believe this is an error, please contact your account manager
              or <Link to="/login">try logging in</Link>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="setup-container">
        <div className="setup-card">
          <div className="setup-success">
            <div className="success-icon">✓</div>
            <h2>Account Activated!</h2>
            <p>Your account has been set up successfully.</p>
            <p>Redirecting to login...</p>
            <Link to="/login" className="setup-button">Go to Login</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-container">
      <div className="setup-card">
        <div className="setup-header">
          <h1>Welcome!</h1>
          <p className="dealership-name">{dealershipName}</p>
          <p className="setup-subtitle">Set up your account to access your inventory portal</p>
        </div>

        <form onSubmit={handleSubmit} className="setup-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              disabled={submitting}
            />
            <p className="form-help">This will be your login email</p>
          </div>

          <div className="form-group">
            <label htmlFor="password">Create Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              disabled={submitting}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="setup-button"
            disabled={submitting}
          >
            {submitting ? 'Activating...' : 'Activate Account'}
          </button>
        </form>

        <div className="setup-footer">
          <p>Already have an account? <Link to="/login">Log in</Link></p>
        </div>
      </div>
    </div>
  );
}

export default Setup;
